import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell, BellRing, FileText, Building2, Search, Mail, CheckCheck, Gavel, Shield } from 'lucide-react'
import { notificationsApi, NotificationItem } from '@services/api'
import {
  isPushSupported,
  getActualPushState,
  subscribeToPush,
  unsubscribeFromPush,
} from '@services/pushNotifications'
import type { PushState } from '@services/pushNotifications'

const TYPE_ICON: Record<string, typeof Bell> = {
  ACCESS_GRANTED: Building2,
  DOSSIER_UPDATE: Building2,
  INTEREST_SALES_NOTIFY: Building2,
  INTEREST_EXPRESSED: Search,
  CONTRACT_SIGNED_SALES: FileText,
  CONTRACT_SIGNED_WELCOME: FileText,
  COUNTERSIGN_READY: FileText,
  COUNTERSIGN_REMINDER: FileText,
  SIGNATURE_REQUIRED: FileText,
  NDA_CONFIRMED: Shield,
  BIDDING_ROUND_OPENED: Gavel,
  BIDDING_ROUND_CLOSED: Gavel,
  BID_SUBMITTED: Gavel,
  BID_SUBMITTED_SALES: Gavel,
  BID_WON: Gavel,
  BID_LOST: Gavel,
  BIDDING_ROUND_CLOSING_SOON: Gavel,
  AI_EXTRACTION: FileText,
  AI_EXTRACTION_COMPLETE: FileText,
}

function getTargetPath(n: NotificationItem): string {
  switch (n.notification_type) {
    case 'ACCESS_GRANTED':
    case 'DOSSIER_UPDATE':
    case 'INTEREST_SALES_NOTIFY':
      return n.property_id ? `/properties/${n.property_id}` : '/dashboard'
    case 'CONTRACT_SIGNED_SALES':
    case 'COUNTERSIGN_READY':
    case 'COUNTERSIGN_REMINDER':
    case 'SIGNATURE_REQUIRED':
      return '/sales/contracts'
    case 'INTEREST_EXPRESSED':
    case 'CONTRACT_SIGNED_WELCOME':
    case 'BID_SUBMITTED':
      return n.property_id ? `/properties/${n.property_id}` : '/investor/search'
    case 'NDA_CONFIRMED':
    case 'BIDDING_ROUND_OPENED':
    case 'BIDDING_ROUND_CLOSING_SOON':
    case 'BIDDING_ROUND_CLOSED':
    case 'BID_SUBMITTED_SALES':
    case 'BID_WON':
    case 'BID_LOST':
      return n.property_id ? `/properties/${n.property_id}` : '/dashboard'
    case 'AI_EXTRACTION':
    case 'AI_EXTRACTION_COMPLETE':
      return '/sales/ai-jobs'
    default:
      return '/dashboard'
  }
}

function timeAgo(dateStr: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return t('notifications.timeAgo.justNow')
  if (minutes < 60) return t('notifications.timeAgo.minutes', { count: minutes })
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return t('notifications.timeAgo.hours', { count: hours })
  const days = Math.floor(hours / 24)
  return t('notifications.timeAgo.days', { count: days })
}

export default function NotificationBell() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [pushState, setPushState] = useState<PushState>('default')
  const [pushToggling, setPushToggling] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const supported = isPushSupported()

  // Sync with real browser push state on mount + when dropdown opens
  const syncPushState = useCallback(async () => {
    const state = await getActualPushState()
    setPushState(state)
  }, [])

  useEffect(() => { syncPushState() }, [syncPushState])
  useEffect(() => { if (open) syncPushState() }, [open, syncPushState])

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.list({ page_size: 20 }).then(r => r.data),
    refetchInterval: 60000,
  })

  const unread = data?.unread_count ?? 0
  const items = data?.items ?? []

  // Close on outside click
  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  async function handleItemClick(n: NotificationItem) {
    if (!n.read_at) {
      await notificationsApi.markAsRead(n.id)
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    }
    setOpen(false)
    navigate(getTargetPath(n))
  }

  async function handleMarkAllRead() {
    try {
      await notificationsApi.markAllAsRead()
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    } catch (err) {
      console.error('Failed to mark all as read:', err)
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    }
  }

  async function handlePushToggle() {
    setPushToggling(true)
    try {
      if (pushState === 'active') {
        await unsubscribeFromPush()
      } else {
        await subscribeToPush()
      }
      await syncPushState()
    } finally {
      setPushToggling(false)
    }
  }

  // Bell icon appearance based on push state
  const bellIcon = (() => {
    if (!supported || pushState === 'unsupported' || pushState === 'default') {
      return { Icon: Bell, color: 'text-gray-500 hover:text-blue-600', indicator: null }
    }
    if (pushState === 'denied') {
      return { Icon: Bell, color: 'text-gray-400 hover:text-gray-500', indicator: null }
    }
    if (pushState === 'active') {
      return { Icon: BellRing, color: 'text-blue-600 hover:text-blue-700', indicator: 'bg-green-400' }
    }
    // inactive (permission granted but not subscribed)
    return { Icon: Bell, color: 'text-gray-500 hover:text-blue-600', indicator: null }
  })()

  // Toggle visibility: show when supported and not denied
  const showToggle = supported && pushState !== 'unsupported'

  // Toggle label + title
  const toggleLabel = (() => {
    if (pushState === 'denied') return t('notifications.pushBlocked')
    if (pushState === 'active') return t('notifications.pushEnabled')
    return t('notifications.pushEnable')
  })()

  const toggleTitle = (() => {
    if (pushState === 'denied') return t('notifications.pushBlocked')
    if (pushState === 'active') return t('notifications.pushDisable')
    return t('notifications.pushEnable')
  })()

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell-open-btn-1"
        onClick={() => setOpen(!open)}
        className={`relative p-2 ${bellIcon.color} hover:bg-blue-50 rounded-lg transition-colors`}
        title={t('notifications.title')}
      >
        <bellIcon.Icon size={20} />
        {/* Push-active indicator dot (bottom-right) */}
        {bellIcon.indicator && (
          <span className={`absolute bottom-1 right-1 w-2 h-2 ${bellIcon.indicator} rounded-full ring-2 ring-white`} />
        )}
        {/* Unread badge (top-right) */}
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-x-3 top-16 sm:absolute sm:inset-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-[420px] flex flex-col">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">
              {t('notifications.title')}
            </h3>
            <div className="flex items-center gap-2">
              {showToggle && (
                <button
                  data-testid="notification-bell-push-toggle-btn"
                  onClick={handlePushToggle}
                  disabled={pushToggling || pushState === 'denied'}
                  className={`flex items-center gap-1 text-xs disabled:opacity-50 ${
                    pushState === 'denied'
                      ? 'text-amber-500 cursor-not-allowed'
                      : pushState === 'active'
                        ? 'text-green-600 hover:text-red-500'
                        : 'text-gray-500 hover:text-blue-600'
                  }`}
                  title={toggleTitle}
                >
                  {pushState === 'denied' ? (
                    <Bell size={14} />
                  ) : pushState === 'active' ? (
                    <BellRing size={14} />
                  ) : (
                    <Bell size={14} />
                  )}
                  <span className="hidden sm:inline">
                    {toggleLabel}
                  </span>
                </button>
              )}
              {unread > 0 && (
                <button
                  data-testid="notification-bell-mark-all-read-btn"
                  onClick={handleMarkAllRead}
                  className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                >
                  <CheckCheck size={14} />
                  {t('notifications.markAllRead')}
                </button>
              )}
            </div>
          </div>

          {/* Items */}
          <div className="overflow-y-auto flex-1">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                <Mail size={24} className="mx-auto mb-2 text-gray-300" />
                {t('notifications.empty')}
              </div>
            ) : (
              items.map((n) => {
                const Icon = TYPE_ICON[n.notification_type] || Bell
                const isUnread = !n.read_at
                return (
                  <button
                    data-testid="notification-bell-item-click-btn"
                    key={n.id}
                    onClick={() => handleItemClick(n)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-50 flex items-start gap-3 ${
                      isUnread ? 'bg-blue-50/40' : ''
                    }`}
                  >
                    <div className="mt-0.5 text-gray-400">
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 line-clamp-2">
                          {n.summary || t(`notifications.types.${n.notification_type}`)}
                        </span>
                        {isUnread && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                        )}
                      </div>
                      {(n.triggered_by_name || n.property_title) && (
                        <div className="text-xs text-gray-600 mt-0.5 truncate">
                          {n.triggered_by_name && <span className="font-medium">{n.triggered_by_name}</span>}
                          {n.triggered_by_name && n.property_title && <span className="text-gray-400"> · </span>}
                          {n.property_title && <span>{n.property_title}</span>}
                        </div>
                      )}
                      <div className="text-xs text-gray-500 mt-0.5">
                        {timeAgo(n.created_at, t)}
                      </div>
                    </div>
                  </button>
                )
              })
            )}
          </div>

          {/* Footer: View All link */}
          <button
            data-testid="notification-bell-open-btn-2"
            onClick={() => { setOpen(false); navigate('/notifications') }}
            className="w-full px-4 py-2.5 text-center text-xs font-medium text-primary-600 hover:bg-gray-50 border-t border-gray-100 transition-colors"
          >
            {t('notifications.viewAll', 'Alle anzeigen')}
          </button>
        </div>
      )}
    </div>
  )
}
